import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

/**
 * Dialog to set/clear a scheduled publish and/or unpublish date for an entry.
 * Saving sends both keys — a null value clears that schedule on the backend.
 */
const SchedulePublishModal = ({ open, onClose, schedule, isPublished, onSave, loading = false }) => {
    const [publishAt, setPublishAt] = useState(null);
    const [unpublishAt, setUnpublishAt] = useState(null);
    const [validationError, setValidationError] = useState('');

    // Re-seed pickers from the entry's current schedule each time the dialog opens
    useEffect(() => {
        if (open) {
            setPublishAt(schedule?.scheduledAt ? new Date(schedule.scheduledAt) : null);
            setUnpublishAt(schedule?.scheduledUnpublishAt ? new Date(schedule.scheduledUnpublishAt) : null);
            setValidationError('');
        }
    }, [open, schedule]);

    const validate = () => {
        const now = Date.now();
        if (publishAt && isNaN(publishAt.getTime())) return 'Publish date is not valid';
        if (unpublishAt && isNaN(unpublishAt.getTime())) return 'Unpublish date is not valid';
        if (publishAt && publishAt.getTime() <= now) return 'Publish date must be in the future';
        if (unpublishAt && unpublishAt.getTime() <= now) return 'Unpublish date must be in the future';
        if (publishAt && unpublishAt && unpublishAt.getTime() <= publishAt.getTime()) {
            return 'Unpublish date must be after the publish date';
        }
        return '';
    };

    const handleSave = () => {
        const error = validate();
        if (error) {
            setValidationError(error);
            return;
        }
        setValidationError('');
        onSave({
            publishAt: publishAt ? publishAt.toISOString() : null,
            unpublishAt: unpublishAt ? unpublishAt.toISOString() : null,
        });
    };

    return (
        <Dialog open={open} onClose={loading ? null : onClose} fullWidth maxWidth="sm">
            <DialogTitle display="flex" alignItems="center" gap={1}>
                <ScheduleIcon />
                Schedule Publishing
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Pick when this entry should go live and/or be taken down. Times are
                    shown in your local timezone. Clearing a date cancels that schedule.
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1, mb: 2 }}>
                        <DateTimePicker
                            label={isPublished ? 'Publish at (entry is already published)' : 'Publish at'}
                            value={publishAt}
                            onChange={(value) => setPublishAt(value)}
                            disablePast
                            disabled={loading}
                            sx={{ width: '100%' }}
                        />
                        <Tooltip title="Clear publish schedule">
                            <span>
                                <IconButton
                                    onClick={() => setPublishAt(null)}
                                    disabled={loading || !publishAt}
                                    aria-label="clear publish schedule"
                                >
                                    <ClearIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <DateTimePicker
                            label="Unpublish at"
                            value={unpublishAt}
                            onChange={(value) => setUnpublishAt(value)}
                            disablePast
                            disabled={loading}
                            sx={{ width: '100%' }}
                        />
                        <Tooltip title="Clear unpublish schedule">
                            <span>
                                <IconButton
                                    onClick={() => setUnpublishAt(null)}
                                    disabled={loading || !unpublishAt}
                                    aria-label="clear unpublish schedule"
                                >
                                    <ClearIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </LocalizationProvider>

                {validationError && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {validationError}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ScheduleIcon />}
                >
                    {loading ? 'Saving...' : 'Save Schedule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SchedulePublishModal;
